/**
 * Collapsed (desktop) sidebar content. Extracted verbatim (behavior-preserving)
 * from the `!isMobile && isCollapsed` branch in layout/Sidebar.tsx — renders
 * first-letter avatars for chats and emoji icons for modules. Purely
 * presentational; the orchestrator selects this branch and threads in the
 * already-computed conversation/module lists and the click handlers.
 */

import type { Conversation } from '@/types/conversations';
import { DEFAULT_MODULE_ICONS } from '../constants';
import type { SidebarModuleItem } from '../types';

interface CollapsedSidebarContentProps {
  displayedConversations: Conversation[];
  activeConversationId: string | undefined;
  filteredModules: SidebarModuleItem[];
  onConversationClick: (conversation: Conversation) => void;
  onOpenModule: (item: SidebarModuleItem) => void;
}

export function CollapsedSidebarContent({
  displayedConversations,
  activeConversationId,
  filteredModules,
  onConversationClick,
  onOpenModule,
}: CollapsedSidebarContentProps) {
  return (
    <div className="sidebar-unified-content">
      {/* Collapsed Chats */}
      {displayedConversations.length > 0 && (
        <div className="sidebar-section">
          <nav className="conversation-list">
            {displayedConversations.map((conversation) => {
              const firstLetter = (conversation.name || '?').charAt(0);
              const isActive = conversation.id === activeConversationId;
              return (
                <div
                  key={conversation.id}
                  className={`collapsed-item${isActive ? ' active' : ''}`}
                  onClick={() => onConversationClick(conversation)}
                  title={conversation.name}
                >
                  <span className="collapsed-chat-avatar">{firstLetter}</span>
                </div>
              );
            })}
          </nav>
        </div>
      )}

      {/* Collapsed Modules */}
      {filteredModules.length > 0 && (
        <>
          {displayedConversations.length > 0 && <div className="collapsed-section-divider" />}
          <div className="sidebar-section">
            <nav className="conversation-list sidebar-all-modules-list">
              {filteredModules.map((item) => {
                const config = (item.module.config ?? {}) as Record<string, unknown>;
                const customIcon = (config.icon as string) || null;
                const displayIcon = customIcon || DEFAULT_MODULE_ICONS[item.module.type] || '📦';
                const isCustom = !!customIcon;
                return (
                  <div
                    key={item.module.id}
                    className="collapsed-item"
                    onClick={() => onOpenModule(item)}
                    title={item.displayName}
                  >
                    <span className={`collapsed-module-icon${isCustom ? '' : ' default'}`}>{displayIcon}</span>
                  </div>
                );
              })}
            </nav>
          </div>
        </>
      )}
    </div>
  );
}
